// Google and Apple sign-in leave the page and come back with no router state, and a login or signup in the request form
// can end on another page. When the visitor's action was meant to finish something that waits in storage (the request the
// /chat landing built, or the request form), the click leaves a one-shot marker in sessionStorage (same tab, gone with
// it). A marker counts only while it is fresh and is deleted the moment it is read, so an abandoned attempt cannot turn a
// later, unrelated login into a send.
export type OAuthIntent = 'chat' | 'form';

export const OAUTH_INTENT_MAX_AGE_MS = 10 * 60 * 1000;

const KEYS: Record<OAuthIntent, string> = {
  chat: 'ofair_chat_oauth',
  form: 'ofair_form_oauth',
};

export const markOAuthIntent = (kind: OAuthIntent): void => {
  try {
    sessionStorage.setItem(KEYS[kind], String(Date.now()));
  } catch (error) {
    console.error('Error saving OAuth intent:', error);
  }
};

// Looks without deleting (the landing page of a redirect is chosen before the visitor leaves)
export const peekOAuthIntent = (kind: OAuthIntent): boolean => {
  try {
    const at = Number(sessionStorage.getItem(KEYS[kind]));
    const age = Date.now() - at;
    return at > 0 && age >= 0 && age < OAUTH_INTENT_MAX_AGE_MS;
  } catch {
    return false;
  }
};

export const clearOAuthIntent = (kind: OAuthIntent): void => {
  try {
    sessionStorage.removeItem(KEYS[kind]);
  } catch (error) {
    console.error('Error clearing OAuth intent:', error);
  }
};

// Reads the marker and deletes it, fresh or not
export const consumeOAuthIntent = (kind: OAuthIntent): boolean => {
  const fresh = peekOAuthIntent(kind);
  clearOAuthIntent(kind);
  return fresh;
};

export const clearAllOAuthIntents = (): void => {
  clearOAuthIntent('chat');
  clearOAuthIntent('form');
};
